import { Box, Paper, Text, ThemeIcon } from "@mantine/core";
import {
  IconArrowsDownUp,
  IconBriefcase2,
  IconBriefcaseOff,
  IconBookmark,
  IconSend,
  type Icon,
} from "@tabler/icons-react";

type DashboardCardTitle =
  | "Requests Sent"
  | "Requests Received"
  | "Active Collaborations"
  | "Closed Collaborations"
  | "Shortlist";

type DashboardCardProps = {
  title: DashboardCardTitle | string;
  count: number;
  description: string;
  onClick?: () => void;
};

type CardVisualConfig = {
  icon: Icon;
  iconColor: string;
  iconBg: string;
  borderColor: string;
  cardBg: string;
  titleColor: string;
};

const DEFAULT_CONFIG: CardVisualConfig = {
  icon: IconArrowsDownUp,
  iconColor: "#2563EB",
  iconBg: "#EFF6FF",
  borderColor: "#E5E7EB",
  cardBg: "#FFFFFF",
  titleColor: "#1D4ED8",
};

const TITLE_VISUALS: Record<string, CardVisualConfig> = {
  "Requests Sent": {
    icon: IconSend,
    iconColor: "#2563EB",
    iconBg: "#EFF6FF",
    borderColor: "#DBEAFE",
    cardBg: "#FFFFFF",
    titleColor: "#2563EB",
  },
  "Requests Received": {
    icon: IconArrowsDownUp,
    iconColor: "#3B82F6",
    iconBg: "#EEF2FF",
    borderColor: "#DBEAFE",
    cardBg: "#FFFFFF",
    titleColor: "#2563EB",
  },
  "Active Collaborations": {
    icon: IconBriefcase2,
    iconColor: "#F59E0B",
    iconBg: "#FEF3C7",
    borderColor: "#FDE68A",
    cardBg: "#FFFBEB",
    titleColor: "#D97706",
  },
  "Closed Collaborations": {
    icon: IconBriefcaseOff,
    iconColor: "#F97316",
    iconBg: "#FFEDD5",
    borderColor: "#FED7AA",
    cardBg: "#FFF7ED",
    titleColor: "#EA580C",
  },
  Shortlist: {
    icon: IconBookmark,
    iconColor: "#7C3AED",
    iconBg: "#F3E8FF",
    borderColor: "#DDD6FE",
    cardBg: "#FAF5FF",
    titleColor: "#6D28D9",
  },
};

export default function DashboardCard({ title, count, description, onClick }: DashboardCardProps) {
  const visual = TITLE_VISUALS[title] ?? DEFAULT_CONFIG;
  const IconComponent = visual.icon;

  return (
    <Paper
      radius="md"
      p="md"
      h="100%"
      onClick={onClick}
      style={{
        border: `1px solid ${visual.borderColor}`,
        backgroundColor: visual.cardBg,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Box style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="sm" c={visual.titleColor}>
            {title}
          </Text>
          <Text fw={700} fz={30} c="#0F172A" lh={1.1} mt={6}>
            {count}
          </Text>
          <Text size="xs" c="#64748B" mt={10} style={{ lineHeight: 1.35 }}>
            {description}
          </Text>
        </Box>
        <ThemeIcon radius="md" size={32} variant="light" style={{ backgroundColor: visual.iconBg, color: visual.iconColor }}>
          <IconComponent size={18} />
        </ThemeIcon>
      </Box>
    </Paper>
  );
}
